import React, { useState, useEffect } from "react";
import axios from "axios";
import delet from '../Images/delete.png';
import edit from '../Images/edit.png';
import Select from 'react-select';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json'; // or ar.json for Arabic
countries.registerLocale(enLocale); 

const AllAddresses = ({ token }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false); 
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(''); 
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    county: '',
    postcode: '',
    street: '',
    building: '',
    floor: '',
    apartment: '',
    phone_number: '',
    note: '',
    isDefault: true,
    with_logo: true,
  });
  const [postcodeError, setPostcodeError] = useState(''); // Error message for postcode validation
  const [geolocation, setGeolocation] = useState({ latitude: '', longitude: '' }); // Geolocation data
  const countryOptions = Object.entries(countries.getNames('en')).map(([code, name]) => ({
    value: name,
    label: name,
  }));

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Fetch addresses on component mount
 
    const fetchAddresses = async () => {
      try {
        const response = await axios.get(
          'https://backend.printtrio.co.uk/api/profile/get-addresses',
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          setAddresses(response.data.data); // Save addresses to state
        } else {
          setError('Failed to fetch addresses. Please try again.');
        }
      } catch (err) {
        setError('An error occurred while fetching addresses.');
      } finally {
        setLoading(false); // Set loading to false after data is fetched
      }
    };
    useEffect(() => {
    if (token) {
      fetchAddresses();
    }
  }, [token]);

 const confirmDelete = (id) => {
    setAddressToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!addressToDelete) return;

    try {
      const response = await fetch(
        `https://backend.printtrio.co.uk/api/profile/delete-address/${addressToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setAddresses((prevAddresses) => prevAddresses.filter((address) => address.id !== addressToDelete));
        setMessage('Address deleted successfully!');
      } else {
        setMessage("Failed to delete the address. Please try again.");
      }
    } catch (error) {
      setMessage("An error occurred while deleting the address.");
    } finally {
      setShowDeleteConfirm(false);
      setAddressToDelete(null);
    }
  };
  
  const handlePostcodeLookup = async () => {
    const postcode = selectedAddress?.postcode?.trim() || formData.postcode?.trim();
    const country = selectedAddress?.country || formData.county;
  
    if (!postcode) {
      if (country === "United Kingdom") {
        setPostcodeError("Postcode is required for United Kingdom.");
      } else {
        setPostcodeError(""); // Clear for non-UK
      }
      return;
    }
  
    try {
      const response = await axios.get(`https://api.postcodes.io/postcodes/${postcode}`);
      if (response.data.status === 200) {
        const { latitude, longitude, admin_district, country } = response.data.result;
        setGeolocation({ latitude, longitude });
  
        if (selectedAddress) {
          setSelectedAddress((prev) => ({
            ...prev,
            city: admin_district,
            country,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            city: admin_district,
            county: country,
          }));
        }
  
        setPostcodeError("");
      } else {
        if (country === "United Kingdom") {
          setPostcodeError("Invalid postcode. Please check and try again.");
        }
      }
    } catch (err) {
      if (country === "United Kingdom") {
        setPostcodeError("Please provide a valid postcode in UK.");
      }
    }
  };

  useEffect(() => {
    if (
      selectedAddress &&
      selectedAddress.country === "United Kingdom" &&
      selectedAddress.postcode.trim()
    ) {
      const delay = setTimeout(() => {
        handlePostcodeLookup();
      }, 800);
  
      return () => clearTimeout(delay);
    }
  }, [selectedAddress?.postcode, selectedAddress?.country]);
  
  
  useEffect(() => {
    if (!formData.postcode.trim()) return;
  
    const delay = setTimeout(() => {
      handlePostcodeLookup();
    }, 800); 
  
    return () => clearTimeout(delay); 
  }, [formData.postcode]);



  const handleEdit = (id) => {
    const address = addresses.find((addr) => addr.id === id);
    setSelectedAddress(address); // Set the selected address data
    setShowEditModal(true); // Show the modal
  };

  const handleSaveEdit = async () => {
    if (!selectedAddress) return;

    try {
      setIsSaving(true);
      const response = await axios.post(
        `https://backend.printtrio.co.uk/api/profile/edit-address/${selectedAddress.id}`,
        {
          name: selectedAddress.name,
          country: selectedAddress.country,
          city: selectedAddress.city,
          postcode: selectedAddress.postcode,
          street: " ",
          building: selectedAddress.building,
          floor: " ",
          apartment: selectedAddress.apartment,
          phone_number: selectedAddress.phone_number,
          note: selectedAddress.note,
          is_default: selectedAddress.is_default,
          with_logo: selectedAddress.with_logo,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // Update the address in the state
        setAddresses((prevAddresses) =>
          prevAddresses.map((addr) =>
            addr.id === selectedAddress.id ? { ...addr, ...selectedAddress } : addr
          )
        );
        setShowEditModal(false); // Close the modal
        setMessage('Address updated successfully!'); // Set success message
      fetchAddresses();
      } else {
        alert('Failed to edit the address. Please try again.');
      }
    } catch (error) {
      alert('An error occurred while editing the address.');
    } finally {
      setIsSaving(false);
    }
  };

  // const closeModal = () => {
  //   setShowEditModal(false); // Hide the modal
  //   setSelectedAddress(null); // Clear the selected address
  // };


  if (loading) {
    return <div className="flex-grow p-10 bg-white mt-[3%] mb-[3%] mx-[5%] text-center py-10">Loading addresses...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  return (
    <div className="flex-grow p-10 bg-white mt-[3%] mb-[3%] mx-[5%]">
    <div className="p-8 bg-white">
      <h2 className="text-xl font-bold mb-6">All Addresses</h2>
      {addresses.length === 0 ? (
        <p className="text-gray-500">No addresses found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 z-0 relative">
        {addresses.map((address) => (
          <div
            key={address.id}
            className="relative border border-gray-300 rounded-lg p-4 shadow-sm bg-[#F9F9F9] z-10"
          >
            {/* Action Buttons */}
            <div className="absolute top-2 right-2 flex gap-2">
              <button onClick={() => handleEdit(address.id)}>
                <img
                  src={edit}
                  alt="Edit"
                  className="w-4 h-4 cursor-pointer hover:opacity-80"
                />
              </button>
              <button onClick={() => confirmDelete(address.id)}>
                <img
                  src={delet}
                  alt="Delete"
                  className="w-4 h-4 cursor-pointer hover:opacity-80"
                />
              </button>
            </div>

            {/* Address Details */}
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              {address.name}
              {address.is_default ? (
                <span className="ml-2 text-sm text-white bg-pink-500 px-2 py-1 rounded-full">
                  Default
                </span>
              ) : null}
            </h3>
            <p className="text-gray-600 text-sm">
              <strong>Address Line 1:</strong> {address.building}
            </p>
            <p className="text-gray-600 text-sm">
              <strong>Address Line 2:</strong> {address.apartment}
            </p>
            <p className="text-gray-600 text-sm">
              <strong>City:</strong> {address.city}
            </p>
            <p className="text-gray-600 text-sm">
              <strong>Postcode:</strong> {address.postcode}
            </p>
            <p className="text-gray-600 text-sm">
              <strong>Country:</strong> {address.country}
            </p>
            
            {/* <p className="text-gray-600 text-sm">
              <strong>Street:</strong> {address.street}
            </p> */}
          
            {/* <p className="text-gray-600 text-sm">
              <strong>Floor:</strong> {address.floor}
            </p> */}
    
            <p className="text-gray-600 text-sm">
              <strong>Phone:</strong>{' '}
              <a
                href={`tel:${String(address.phone_number || '').replace(/[^\d+]/g, '')}`}
                className="text-blue-600 hover:underline"
              >
                {address.phone_number}
              </a>
            </p>
            <p className="text-gray-600 text-sm">
              <strong>Note:</strong> {address.note || 'N/A'}
            </p>
            {address.with_logo ? (
              <p className="text-gray-600 text-sm">
                <strong>Logo:</strong> Included
              </p>
            ) : (
              <p className="text-gray-600 text-sm">
                <strong>Logo:</strong> Not Included
              </p>
            )}
          </div>
        ))}
      </div>
      )}
    </div>

    {showEditModal && selectedAddress && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md max-h-[70vh] overflow-y-auto">
  <h2 className="text-xl font-bold mb-4">Edit Address</h2>

  <div className="grid grid-cols-1 gap-4">
    {/* Name */}
    <div>
      <label className="block text-sm font-medium text-gray-700">Full Name <span className="text-red-500">*</span></label>
      <input
        type="text"
        value={selectedAddress.name}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, name: e.target.value })}
        placeholder="Full Name *"
        className="border rounded-lg p-2 w-full"
        required
      />
    </div>

     <div>
      <label className="block text-sm font-medium text-gray-700">Address Line 1 <span className="text-red-500">*</span></label>
      <input
        type="text"
        value={selectedAddress.building}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, building: e.target.value })}
        placeholder="Building Name/Number *"
        className="border rounded-lg p-2 w-full"
        required
      />
    </div>

        <div>
      <label className="block text-sm font-medium text-gray-700">Address Line 2 </label>
      <input
        type="text"
        value={selectedAddress.apartment}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, apartment: e.target.value })}
        placeholder="Apartment *"
        className="border rounded-lg p-2 w-full"
      />
    </div>
  {/* City */}
  <div>
      <label className="block text-sm font-medium text-gray-700">City <span className="text-red-500">*</span></label>
      <input
        type="text"
        value={selectedAddress.city}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, city: e.target.value })}
        placeholder="City *"
        className="border rounded-lg p-2 w-full"
        required
      />
    </div>


    {/* Postcode */}
    <div>
      <label className="block text-sm font-medium text-gray-700">Postcode <span className="text-red-500">*</span></label>
      <input
        type="text"
        value={selectedAddress.postcode}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, postcode: e.target.value })}
        onBlur={() => {
          if (selectedAddress.country === "United Kingdom") {
            handlePostcodeLookup();
          }
        }}
        placeholder="Postcode *"
        className="border rounded-lg p-2 w-full"
        required
      />
    </div>

        {/* Country */}
    <div>
      <label className="block text-sm font-medium text-gray-700">Country <span className="text-red-500">*</span></label>
      <div className="border rounded-lg p-1 w-full">
        <Select
          options={countryOptions}
          value={countryOptions.find(opt => opt.value === selectedAddress.country)}
          onChange={(selected) =>
            setSelectedAddress((prev) => ({ ...prev, country: selected.value }))
          }
          placeholder="Select Country"
          classNamePrefix="select"
          styles={{
            control: (base) => ({ ...base, border: 'none', boxShadow: 'none' }),
            placeholder: (base) => ({ ...base, textAlign: 'left', color: '#9CA3AF' }),
            singleValue: (base) => ({ ...base, textAlign: 'left' }),
            input: (base) => ({ ...base, textAlign: 'left' }),
          }}
        />
      </div>
    </div>

  
  

    

    {/* Street */}
    {/* <div>
      <label className="block text-sm font-medium text-gray-700">Street <span className="text-red-500">*</span></label>
      <input
        type="text"
        value={selectedAddress.street}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, street: e.target.value })}
        placeholder="Street *"
        className="border rounded-lg p-2 w-full"
        required
      />
    </div> */}

    {/* Building */}
   

    {/* Floor */}
    {/* <div>
      <label className="block text-sm font-medium text-gray-700">Floor <span className="text-red-500">*</span></label>
      <input
        type="text"
        value={selectedAddress.floor}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, floor: e.target.value })}
        placeholder="Floor *"
        className="border rounded-lg p-2 w-full"
        required
      />
    </div> */}

    {/* Apartment */}


    {/* Phone Number */}
    <div>
      <label className="block text-sm font-medium text-gray-700">Phone Number <span className="text-red-500">*</span></label>
      <input
        type="text"
        value={selectedAddress.phone_number}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, phone_number: e.target.value })}
        placeholder="Phone Number *"
        className="border rounded-lg p-2 w-full"
        required
      />
    </div>

    {/* Notes */}
    <div>
      <label className="block text-sm font-medium text-gray-700">Additional Notes</label>
      <textarea
        value={selectedAddress.note || ''}
        onChange={(e) => setSelectedAddress({ ...selectedAddress, note: e.target.value })}
        placeholder="Additional Notes (optional)"
        className="border rounded-lg p-2 w-full h-20"
      ></textarea>
    </div>

    {/* Checkboxes */}
    <div className="flex items-center gap-6">
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={selectedAddress.with_logo}
          onChange={(e) =>
            setSelectedAddress({ ...selectedAddress, with_logo: e.target.checked })
          }
          className="mr-2 h-5 w-5 accent-pinkCustom border-gray-300 rounded"
        />
        <label className="text-sm font-medium text-gray-700">With Printtrio Logo</label>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          checked={selectedAddress.is_default}
          onChange={(e) =>
            setSelectedAddress({ ...selectedAddress, is_default: e.target.checked })
          }
          className="mr-2 h-5 w-5 accent-pinkCustom border-gray-300 rounded"
        />
        <label className="text-sm font-medium text-gray-700">Set as Default</label>
      </div>
    </div>

    {/* Buttons */}
    <div className="flex justify-end space-x-4 mt-2">
      <button
        className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
        onClick={() => setShowEditModal(false)}
      >
        Cancel
      </button>
      <button
        className={`px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 ${
          isSaving ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={handleSaveEdit}
        disabled={isSaving}
      >
        {isSaving ? 'Saving...' : 'Save'}
      </button>
    </div>
  </div>
</div>

  </div>
)}

{showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm">
            <h2 className="text-xl font-bold mb-4">Delete Address</h2>
            <p>Are you sure you want to delete this address?</p>
            <div className="flex justify-end mt-6 space-x-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setAddressToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AllAddresses;
